import React, { useState } from 'react'
import { Container, Row, Col, FloatingLabel, Form, Button, ListGroup } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPhone, faEnvelope, faLocationDot } from '@fortawesome/free-solid-svg-icons'
import { Helmet } from 'react-helmet'
import about from '../../assets/img/slides/Abouts.png'
import './contact.css'

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  })

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: false
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormStatus({ submitting: true, submitted: false, error: false })
    
    // Simulate form submission
    setTimeout(() => {
      setFormStatus({ submitting: false, submitted: true, error: false })
      setFormData({ name: '', email: '', phone: '', message: '' })
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setFormStatus({ submitting: false, submitted: false, error: false })
      }, 5000)
    }, 1000)
  }

  return (
    <div className="contact-page">
      <Helmet>
        <title>Contact YorkeTV | Get In Touch</title>
        <meta name="description" content="Contact YorkeTV - Get in touch with us for inquiries, support, or feedback" />
        <meta name="keywords" content="YorkeTV, Contact YorkeTV, Channel 7 Contact" />
        <meta name="author" content="Groupe Nduom Station Based In UK" />
        <link rel="canonical" href="https://www.yorketv.com/contact" />
      </Helmet>

      {/* Hero Section */}
      <div className="contact-hero" style={{ backgroundImage: `url(${about})` }}>
        <div className="contact-hero-overlay">
          <div className="contact-hero-content">
            <h6 className="contact-hero-subtitle">Get In Touch</h6>
            <h1 className="contact-hero-title">Contact Us</h1>
            <p className="contact-hero-description">
              Have questions? Need to reach us? We're here to help!
            </p>
          </div>
        </div>
      </div>

      <Container>
        <Row className="contact-content g-4">
          {/* Contact Form */}
          <Col xs={12} lg={6} md={6} className="contact-form-col">
            <div className="contact-form-section">
              <h2 className="form-section-title">Send Us a Message</h2>
              <p className="form-section-description">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>

              {formStatus.submitted && (
                <div className="alert alert-success form-alert">
                  <strong>Thank you!</strong> Your message has been sent successfully. We'll get back to you soon.
                </div>
              )}

              {formStatus.error && (
                <div className="alert alert-danger form-alert">
                  <strong>Error!</strong> There was a problem sending your message. Please try again.
                </div>
              )}

              <form id="contactForm" onSubmit={handleSubmit} className="contact-form">
                <FloatingLabel
                  controlId="floatingName"
                  label="Your Name"
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    name="name"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </FloatingLabel>

                <FloatingLabel
                  controlId="floatingEmail"
                  label="Email Address"
                  className="mb-3"
                >
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="name@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </FloatingLabel>

                <FloatingLabel
                  controlId="floatingPhone"
                  label="Phone Number"
                  className="mb-3"
                >
                  <Form.Control
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </FloatingLabel>

                <FloatingLabel
                  controlId="floatingMessage"
                  label="Your Message"
                  className="mb-4"
                >
                  <Form.Control
                    as="textarea"
                    name="message"
                    placeholder="Leave your message here"
                    style={{ height: '150px' }}
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </FloatingLabel>

                <Button
                  type="submit"
                  className="contact-submit-btn"
                  disabled={formStatus.submitting}
                >
                  {formStatus.submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </Col>

          {/* Contact Information */}
          <Col xs={12} lg={6} md={6} className="contact-info-col">
            <div className="contact-info-section">
              <h2 className="info-section-title">Contact Information</h2>
              <p className="info-section-description">
                Reach out to us through any of these channels
              </p>

              <div className="contact-info-list">
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <FontAwesomeIcon icon={faPhone} />
                  </div>
                  <div className="contact-info-content">
                    <h5 className="contact-info-label">Phone</h5>
                    <a href="tel:+447852901488" className="contact-info-value">
                      +44 7852 901488
                    </a>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </div>
                  <div className="contact-info-content">
                    <h5 className="contact-info-label">Email</h5>
                    <a href="mailto:info@mychannel7tv.co.uk" className="contact-info-value">
                      info@mychannel7tv.co.uk
                    </a>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <FontAwesomeIcon icon={faLocationDot} />
                  </div>
                  <div className="contact-info-content">
                    <h5 className="contact-info-label">Address</h5>
                    <p className="contact-info-value">
                      Vox Studios Unit WS<br />
                      London, UK
                    </p>
                  </div>
                </div>
              </div>

              <div className="contact-hours">
                <h5 className="hours-title">Business Hours</h5>
                <div className="hours-list">
                  <div className="hours-item">
                    <span className="hours-day">Monday - Friday</span>
                    <span className="hours-time">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="hours-item">
                    <span className="hours-day">Saturday</span>
                    <span className="hours-time">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="hours-item">
                    <span className="hours-day">Sunday</span>
                    <span className="hours-time">Closed</span>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default Contact
